import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import apiMap from '../../apiMapping.json';
import './SlokaSummary.css'
import FetchError from '../FetchError/FetchError';
import { createCachedGet, invalidateCachedUrl } from '../../utils/apiCache';

const cachedGet = createCachedGet(axios);

function SlokaSummary({ audio, language, chapterNumber, slokaNumber }) {
  const [yogaName, setYogaName] = useState('');
  const [chapterName, setChapterName] = useState('');
  const [meaning, setMeaning] = useState('');
  const [summary, setSummary] = useState([]);
  const [commentary, setCommentary] = useState([]);
  const [synonym, setSynonym] = useState([]);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [copyStatus, setCopyStatus] = useState('idle');

  useEffect(() => {
    if (!chapterNumber || !slokaNumber) return;

    const controller = new AbortController();
    const { signal } = controller;
    const base = apiMap.hostData + apiMap.apiData;
    const chapterUrl = `${base}chapter/${language}/${chapterNumber}`;
    const summaryUrl = `${base}sloka/${language}/${chapterNumber}/${slokaNumber}`;
    const commentaryUrl = `${base}commentary/${language}/${chapterNumber}/${slokaNumber}`;
    const synonymUrl = `${base}synonym/${language}/${chapterNumber}/${slokaNumber}`;

    const fetchData = async () => {
      try {
        setLoaded(false);
        setError(null);

        const [chapterRes, summaryRes, commentaryRes, synonymRes] = await Promise.all([
          cachedGet(chapterUrl, { signal }),
          cachedGet(summaryUrl, { signal }),
          cachedGet(commentaryUrl, { signal }),
          cachedGet(synonymUrl, { signal }),
        ]);

        const chaptersData = Array.isArray(chapterRes.data) ? chapterRes.data : [chapterRes.data];
        if (chaptersData.length > 0) {
          setYogaName(chaptersData[0].yogaName || '');
          setChapterName(chaptersData[0].chapterName || '');
          setMeaning(chaptersData[0].meaning || '');
        }

        setSummary(Array.isArray(summaryRes.data) ? summaryRes.data : [summaryRes.data]);
        setCommentary(Array.isArray(commentaryRes.data) ? commentaryRes.data : [commentaryRes.data]);
        setSynonym(Array.isArray(synonymRes.data) ? synonymRes.data : [synonymRes.data]);

        setLoaded(true);
      } catch (err) {
        if (axios.isCancel?.(err) || err.code === 'ERR_CANCELED' || err.name === 'CanceledError') {
          return;
        }
        setError('Could not load this verse. Try another language or verse.');
        console.error(err);
      }
    };

    fetchData();
    return () => controller.abort();
  }, [language, chapterNumber, slokaNumber, retryKey]);

  const handleRetry = useCallback(() => {
    const base = apiMap.hostData + apiMap.apiData;
    invalidateCachedUrl(`${base}chapter/${language}/${chapterNumber}`);
    invalidateCachedUrl(`${base}sloka/${language}/${chapterNumber}/${slokaNumber}`);
    invalidateCachedUrl(`${base}commentary/${language}/${chapterNumber}/${slokaNumber}`);
    invalidateCachedUrl(`${base}synonym/${language}/${chapterNumber}/${slokaNumber}`);
    setRetryKey((k) => k + 1);
  }, [language, chapterNumber, slokaNumber]);

  const copyVerse = useCallback(async () => {
    const text = summary.map((item) => item.sloka).filter(Boolean).join('\n\n');
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus('copied');
      window.setTimeout(() => setCopyStatus('idle'), 2000);
    } catch (e) {
      setCopyStatus('error');
      window.setTimeout(() => setCopyStatus('idle'), 2500);
    }
  }, [summary]);

  function nth(n) { return ["st", "nd", "rd"][((n + 90) % 100 - 10) % 10 - 1] || "th"; }

  if (error) {
    return <FetchError message={error} onRetry={handleRetry} />;
  }

  const backgroundImageUrl = `https://thegita.org.in/images/chapters/${chapterNumber}.jpeg`;
  const verseAudioUrl = `https://thegita.org.in/audio/slokas/${audio}/Chapter${chapterNumber}/${chapterNumber}_${slokaNumber}.mp3`;
  const meaningAudioUrl = `https://thegita.org.in/audio/meanings/${language}/${chapterNumber}/Chapter${chapterNumber}_Sloka_${slokaNumber}.mp3`;

  const firstSlokaText = summary.length > 0 ? summary[0].sloka : '';

  return loaded ? (
    <article className="verse-article print-friendly">
      <header
        className="verse-hero"
        style={{
          backgroundImage: `linear-gradient(165deg, rgba(12, 10, 8, 0.25) 0%, rgba(12, 10, 8, 0.92) 100%), url(${backgroundImageUrl})`,
        }}
      >
        <div className="verse-hero__inner">
          <p className="verse-hero__yoga" lang="sa">{yogaName}</p>
          <div className="verse-hero__row">
            <div className="verse-hero__chapter-block">
              <h1 className="verse-hero__chapter-name">{chapterName}</h1>
              {meaning ? <p className="verse-hero__chapter-gloss">{meaning}</p> : null}
            </div>
            <div className="verse-hero__verse-label" aria-label={`Verse ${slokaNumber}`}>
              <span className="verse-hero__verse-num">{slokaNumber}</span>
              <span className="verse-hero__verse-suffix">{nth(slokaNumber)}</span>
              <span className="verse-hero__verse-word">śloka</span>
            </div>
          </div>
        </div>
      </header>

      {summary.length > 0 ? (
        summary.map((item, index) => (
          <div key={index} className="verse-block">
            <div className="verse-om" aria-hidden="true">
              <span className="verse-om__line" />
              <span className="verse-om__symbol">ॐ</span>
              <span className="verse-om__line" />
            </div>

            <div className="verse-text-block">
              {index === 0 ? (
                <div className="verse-toolbar no-print">
                  {firstSlokaText ? (
                    <button
                      type="button"
                      className="verse-toolbar__btn"
                      onClick={copyVerse}
                      aria-label="Copy verse text to clipboard"
                    >
                      {copyStatus === 'copied' ? 'Copied' : copyStatus === 'error' ? 'Copy failed' : 'Copy śloka'}
                    </button>
                  ) : null}
                </div>
              ) : null}

              <blockquote className="verse-text" lang={language === 'en' ? 'en' : undefined}>
                <span className="verse-text__quote" aria-hidden="true">“</span>
                {item.sloka}
                <span className="verse-text__quote" aria-hidden="true">”</span>
              </blockquote>

              <div className="verse-audio no-print">
                <p className="verse-audio__label">Verse recitation</p>
                <audio controls preload="none" src={verseAudioUrl} />
              </div>
            </div>

            <div className="verse-panels">
              <section className="verse-panel verse-panel--meaning" aria-labelledby={`meaning-${chapterNumber}-${slokaNumber}`}>
                <div className="verse-panel__head">
                  <h2 id={`meaning-${chapterNumber}-${slokaNumber}`} className="verse-panel__title">Meaning</h2>
                  <div className="verse-panel__audio no-print">
                    <audio controls preload="none" src={meaningAudioUrl} />
                  </div>
                </div>
                <p className="verse-panel__body">{item.meaning}</p>
              </section>

              <section className="verse-panel verse-panel--commentary" aria-labelledby={`comm-${chapterNumber}-${slokaNumber}`}>
                <h2 id={`comm-${chapterNumber}-${slokaNumber}`} className="verse-panel__title">Commentary</h2>
                {commentary.length > 0 ? (
                  commentary.map((c, cIndex) => (
                    <p key={cIndex} className="verse-panel__body verse-panel__body--comment">{c.commentary}</p>
                  ))
                ) : (
                  <p className="verse-panel__empty">No commentary for this selection.</p>
                )}
              </section>

              <section className="verse-panel verse-panel--synonyms" aria-labelledby={`syn-${chapterNumber}-${slokaNumber}`}>
                <h2 id={`syn-${chapterNumber}-${slokaNumber}`} className="verse-panel__title">Synonyms</h2>
                <div className="syn-table-wrap">
                  <table className="syn-table">
                    <thead>
                      <tr>
                        <th scope="col">Word</th>
                        <th scope="col">Meaning</th>
                      </tr>
                    </thead>
                    <tbody>
                      {synonym.length > 0 ? (
                        synonym.map((syn, sIndex) => (
                          <tr key={sIndex}>
                            <td>{syn.word}</td>
                            <td>{syn.meaning}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={2} className="syn-table__empty">No synonyms listed.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          </div>
        ))
      ) : (
        <p className="verse-empty">No verse text available for this selection.</p>
      )}
    </article>
  ) : (
    <div className="gita-loader gita-loader--tall" aria-busy="true" aria-label="Loading verse">
      <span className="gita-loader__ring" aria-hidden />
      <span>Drawing the verse…</span>
    </div>
  );
}

export default SlokaSummary;
